import { Component, ReactNode } from 'react'
import NetworkErrorModal, {
  NetworkErrorVariant
} from 'shared/molecules/networkErrorModal/NetworkErrorModal'
import { isChunkLoadError } from 'shared/molecules/networkErrorModal/isChunkLoadError'

interface Props {
  children: ReactNode
}

interface State {
  variant: NetworkErrorVariant | null
}

class NetworkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { variant: null }
  }

  public static getDerivedStateFromError(
    error: unknown
  ): Partial<State> | null {
    return isChunkLoadError(error) ? { variant: 'chunk' } : null
  }

  public componentDidMount() {
    window.addEventListener('offline', this.handleOffline)
    window.addEventListener('online', this.handleOnline)
    // Capture phase so resource-load failures (which do not bubble) are caught.
    window.addEventListener('error', this.handleWindowError, true)
    window.addEventListener('unhandledrejection', this.handleRejection)
  }

  public componentDidCatch(error: Error) {
    if (!isChunkLoadError(error)) {
      throw error
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('offline', this.handleOffline)
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('error', this.handleWindowError, true)
    window.removeEventListener('unhandledrejection', this.handleRejection)
  }

  private readonly handleOffline = () => {
    // A hard error (chunk) takes precedence over a transient offline state.
    this.setState((prev) =>
      prev.variant === 'chunk' ? prev : { variant: 'network' }
    )
  }

  private readonly handleOnline = () => {
    // Auto-dismiss the offline modal once the connection is restored.
    this.setState((prev) =>
      prev.variant === 'network' ? { variant: null } : prev
    )
  }

  private readonly handleWindowError = (event: ErrorEvent) => {
    if (isChunkLoadError(event.error)) {
      this.setState({ variant: 'chunk' })
    }
  }

  private readonly handleRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      this.setState({ variant: 'chunk' })
    }
  }

  private readonly handleClose = () => {
    this.setState({ variant: null })
  }

  public render() {
    const { children } = this.props
    const { variant } = this.state
    return (
      <>
        {children}
        <NetworkErrorModal
          open={variant !== null}
          variant={variant ?? 'network'}
          onClose={this.handleClose}
        />
      </>
    )
  }
}

export default NetworkErrorBoundary
